import { LegacyIQuickAddConverter } from "@faire/deprecated-retailer-visitor-shared/components/QuickAdd/LegacyIQuickAddConverter";
import { DEFAULT_CURRENCY } from "@faire/deprecated-retailer-visitor-shared/consts/DEFAULT_CURRENCY";
import {
  transformBrandToLeanBrand,
  transformProductToOptionLessProduct,
} from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductTile/__internal__/adapters/LeanProtos/adapters";
import {
  getBestProductImage,
  getCaseSizeText,
  getLeastExpensiveOptionByRetailPrice,
  getProductLevelPromotion,
  getProductOptionRetailPrice,
  getProductOptionWholesalePrice,
} from "@faire/deprecated-retailer-visitor-shared/lib/product";
import { IBadgeList } from "@faire/web-api--source/indigofair/badges/IBadgeList";
import { IBrand as IFullBrand } from "@faire/web-api--source/indigofair/data/IBrand";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import { ILeanProductTile as ILeanProductTileImport } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import { IProduct as IFullProduct } from "@faire/web-api--source/indigofair/data/IProduct";
import { faireDateRangeFromExpectedShipDateRange } from "@faire/web/common/expectedShipDateRange";
import { PresetDateFormat } from "@faire/web/common/FaireDateRange";
import {
  DEPRECATED_backwardsCompatibleValues,
  StrictLocalizeFunction,
} from "@faire/web/common/localization/localize";

/**
 * This is being re-exported here, in order to avoid double imports
 * ```ts
 * import { ILeanProductTile as ILeanProductTileType } from '@faire/web-api/...'
 * import { ILeanProductTile } from '@retailer/...'
 *
 * ILeanProductTile.from
 * ```
 *
 * Instead you can just import one or the other depending on whether you only need just the type, then import from @faire/web-api
 * Otherwise import from @retailer
 */
export type ILeanProductTile = ILeanProductTileImport;

export interface ILeanProductTileWithBrand extends ILeanProductTile {
  brand: ILeanProductTileImport.IBrand | undefined;
}

export namespace ILeanProductTileWithBrand {
  export const build = (
    partial?: Partial<ILeanProductTileWithBrand> | undefined
  ): ILeanProductTileWithBrand => ({
    brand: ILeanProductTileImport.IBrand.build(partial?.brand),
    ...ILeanProductTileImport.build(partial),
  });
}

export namespace ILeanProductTile {
  export type IBrand = ILeanProductTileImport.IBrand;
  export type ILeanProduct = ILeanProductTileImport.ILeanProduct;
  export const from = (
    product: IFullProduct | undefined,
    strictLocalize: StrictLocalizeFunction,
    brand: IFullBrand | undefined = undefined,
    badgeMap: { [product_token: string]: IBadgeList } = {},
    promoString?: string
  ): ILeanProductTileWithBrand => {
    const best_image = getBestProductImage(product);
    const dateRange = product
      ? faireDateRangeFromExpectedShipDateRange(product)
      : undefined;
    const minOptionWholesalePromoPrice = getProductLevelPromotion(product);

    return ILeanProductTileWithBrand.build({
      brand: !brand ? undefined : transformBrandToLeanBrand(brand),
      product: !product
        ? undefined
        : transformProductToOptionLessProduct(product),
      badge_list: IBadgeList.build(badgeMap[product?.token ?? ""]),
      best_image,
      quick_add: product
        ? new LegacyIQuickAddConverter(product, strictLocalize).toIQuickAdd()
        : undefined,
      min_option_retail_price: product
        ? getProductOptionRetailPrice(product)
        : IFaireMoney.build({
            amount_cents: 0,
            currency: DEFAULT_CURRENCY,
          }),
      min_option_wholesale_price: product
        ? getProductOptionWholesalePrice(product)
        : IFaireMoney.build({
            amount_cents: 0,
            currency: DEFAULT_CURRENCY,
          }),
      expected_shipping_date: dateRange
        ? strictLocalize(
            {
              id: "o0tz12",
              defaultMessage: "Ships {date}",
            },
            DEPRECATED_backwardsCompatibleValues({
              date: dateRange.format({
                presetDateFormat: PresetDateFormat.ABBREVIATED_MMM_D,
              }),
            })
          )
        : undefined,
      min_option_wholesale_promo_price: minOptionWholesalePromoPrice,
      is_highlighted_product_for_promotional_event:
        product?.is_highlighted_product_for_promotional_event,
      case_size_text: product
        ? getCaseSizeText(product, strictLocalize)
        : undefined,
      min_option_brand_code: product
        ? getLeastExpensiveOptionByRetailPrice(product.options)?.brand_code
        : undefined,
      has_active_brand_promo:
        !!brand?.is_highlighted_brand_for_promotional_event,
      brand_promo_text: promoString,
      based_in_country: brand?.based_in,
    });
  };
}
